/**
 * WholesaleComparisonModal Component
 *
 * Custom modal for the multi-location Wholesale Comparison report.
 * Uses month/year picker and location multi-select instead of the
 * standard single-location + date-range ReportModal.
 */

import React, { useState, useMemo } from 'react';
import { XMarkIcon, PlayIcon, DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { ReportDefinition } from '../../types/reports';
import { ReportExportButtons } from './ReportExportButtons';
import { printCurrentPage } from '../../utils/reportExport';
import { runWholesaleComparison, RunReportResponse } from '../../services/api';
import { WholesaleComparisonReport } from './renderers/WholesaleComparisonReport';

// Eligible locations (exclude 44 - Body Shop only, no Parts dept)
const ELIGIBLE_LOCATIONS = [
  { id: 1, name: "Darling's Ford VW Audi" },
  { id: 2, name: "Darling's Honda Nissan Volvo" },
  { id: 3, name: "Darling's Auto Mall" },
  { id: 4, name: "Darling's Chrysler Dodge Hyundai" },
  { id: 5, name: "Darlings Chevrolet" },
  { id: 10, name: "Brunswick Ford" },
  { id: 11, name: "Darlings CDJR Newport" },
  { id: 12, name: "Darling's Green Point" },
  { id: 14, name: "Darling's Kia" },
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

interface WholesaleComparisonModalProps {
  report: ReportDefinition;
  isOpen: boolean;
  onClose: () => void;
}

type ReportStatus = 'idle' | 'running' | 'complete' | 'error';

export const WholesaleComparisonModal: React.FC<WholesaleComparisonModalProps> = ({
  report,
  isOpen,
  onClose,
}) => {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedLocationIds, setSelectedLocationIds] = useState<Set<number>>(
    () => new Set(ELIGIBLE_LOCATIONS.map(l => l.id))
  );

  const [status, setStatus] = useState<ReportStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<RunReportResponse | null>(null);
  const [runningMessage, setRunningMessage] = useState('Generating report...');

  // Build year options (current year back to 2024)
  const yearOptions = useMemo(() => {
    const years: number[] = [];
    for (let y = now.getFullYear(); y >= 2024; y--) {
      years.push(y);
    }
    return years;
  }, [now]);

  const toggleLocation = (id: number) => {
    setSelectedLocationIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedLocationIds(new Set(ELIGIBLE_LOCATIONS.map(l => l.id)));
  };

  const clearAll = () => {
    setSelectedLocationIds(new Set());
  };

  const canRun = selectedLocationIds.size > 0;

  const isRetryableError = (err: unknown): boolean => {
    const msg = err instanceof Error ? err.message.toLowerCase() : '';
    return msg.includes('failed to fetch') || msg.includes('networkerror') ||
      msg.includes('timeout') || msg.includes('504') || msg.includes('503') ||
      msg.includes('network') || msg.includes('aborted') || msg.includes('timed out');
  };

  const handleRunReport = async () => {
    if (!canRun) return;

    setStatus('running');
    setError(null);
    setReportData(null);
    setRunningMessage('Generating report...');

    const warmupTimer = setTimeout(() => {
      setRunningMessage('Warming up connection...');
    }, 5000);

    const MAX_ATTEMPTS = 2;
    let lastError: unknown;

    try {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const response = await runWholesaleComparison(
            report.id,
            Array.from(selectedLocationIds).sort((a, b) => a - b),
            selectedYear,
            selectedMonth
          );

          if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to generate report');
          }

          clearTimeout(warmupTimer);
          setReportData(response.data);
          setStatus('complete');
          return;
        } catch (err) {
          lastError = err;
          if (attempt < MAX_ATTEMPTS && isRetryableError(err)) {
            console.warn(`Report attempt ${attempt} failed, retrying...`, err);
            setRunningMessage('Retrying...');
            continue;
          }
          throw err;
        }
      }
    } catch {
      clearTimeout(warmupTimer);
      setError(lastError instanceof Error ? lastError.message : 'Failed to generate report');
      setStatus('error');
    }
  };

  const handlePrintPDF = () => {
    printCurrentPage();
  };

  const handleExportCSV = () => {
    if (!reportData?.data) return;

    const data = reportData.data as any;
    const rows: string[] = [];

    // Header
    rows.push(`Report: Wholesale Comparison`);
    rows.push(`Period: ${MONTHS[selectedMonth - 1]} ${selectedYear} vs ${selectedYear - 1}`);
    rows.push(`Generated: ${new Date().toLocaleDateString()}`);
    rows.push('');

    // Column headers
    rows.push([
      'Location',
      `${selectedYear} Sales`, `${selectedYear} Cost`, `${selectedYear} Gross`, `${selectedYear} GP%`,
      `${selectedYear - 1} Sales`, `${selectedYear - 1} Cost`, `${selectedYear - 1} Gross`, `${selectedYear - 1} GP%`,
      'Variance Gross', 'Variance %',
    ].join(','));

    // Data rows
    const formatNum = (v: number) => v.toString();
    const formatPct = (v: number | null) => v === null ? '' : `${v.toFixed(1)}%`;

    for (const loc of data.locations) {
      rows.push([
        `"${loc.locationName}"`,
        formatNum(loc.current.sales), formatNum(loc.current.cost), formatNum(loc.current.gross), formatPct(loc.current.gpPct),
        formatNum(loc.prior.sales), formatNum(loc.prior.cost), formatNum(loc.prior.gross), formatPct(loc.prior.gpPct),
        formatNum(loc.variance.gross), formatPct(loc.variance.pct),
      ].join(','));
    }

    // Totals
    rows.push([
      'TOTAL',
      formatNum(data.totals.current.sales), formatNum(data.totals.current.cost), formatNum(data.totals.current.gross), formatPct(data.totals.current.gpPct),
      formatNum(data.totals.prior.sales), formatNum(data.totals.prior.cost), formatNum(data.totals.prior.gross), formatPct(data.totals.prior.gpPct),
      formatNum(data.totals.variance.gross), formatPct(data.totals.variance.pct),
    ].join(','));

    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `wholesale-comparison-${selectedYear}-${String(selectedMonth).padStart(2, '0')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setStatus('idle');
    setError(null);
    setReportData(null);
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  const hasResults = status === 'complete';

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-4 print:p-0 print:overflow-visible print:static"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity print:hidden" />

      {/* Modal */}
      <div className="relative max-w-7xl w-full max-h-[95vh] bg-white rounded-lg shadow-xl flex flex-col print:max-w-none print:max-h-none print:rounded-none print:shadow-none">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between print:border-b-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{report.title}</h2>
            <p className="text-sm text-gray-500 mt-1">
              Counter wholesale performance across locations
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 print:hidden"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 print:overflow-visible">
          {/* Controls */}
          <div className="mb-6 print:hidden">
            <div className="flex flex-wrap items-end gap-6">
              {/* Month/Year Picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
                <div className="flex gap-2">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
                    disabled={status === 'running'}
                    className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                  >
                    {MONTHS.map((name, i) => (
                      <option key={i + 1} value={i + 1}>{name}</option>
                    ))}
                  </select>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                    disabled={status === 'running'}
                    className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                  >
                    {yearOptions.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Run Button */}
              <button
                onClick={handleRunReport}
                disabled={!canRun || status === 'running'}
                className={`
                  inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors
                  ${!canRun || status === 'running'
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                  }
                `}
              >
                {status === 'running' ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {runningMessage}
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-4 w-4 mr-2" />
                    Run Report
                  </>
                )}
              </button>
            </div>

            {/* Location Multi-Select */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  Locations ({selectedLocationIds.size} of {ELIGIBLE_LOCATIONS.length} selected)
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={selectAll}
                    disabled={status === 'running'}
                    className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                  >
                    Select All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={clearAll}
                    disabled={status === 'running'}
                    className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                  >
                    Clear All
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {ELIGIBLE_LOCATIONS.map(loc => (
                  <label
                    key={loc.id}
                    className={`
                      flex items-center gap-2 px-3 py-2 rounded-md border text-sm cursor-pointer transition-colors
                      ${selectedLocationIds.has(loc.id)
                        ? 'border-blue-300 bg-blue-50 text-blue-800'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }
                      ${status === 'running' ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    <input
                      type="checkbox"
                      checked={selectedLocationIds.has(loc.id)}
                      onChange={() => toggleLocation(loc.id)}
                      disabled={status === 'running'}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="truncate">{loc.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 mb-6 print:hidden" />

          {/* Results Area */}
          <div id="report-print-content">
            {/* Print Header */}
            <div className="hidden print:block mb-4">
              <h1 className="text-xl font-bold">Wholesale Comparison</h1>
              <p className="text-sm text-gray-600">
                {MONTHS[selectedMonth - 1]} {selectedYear} vs {selectedYear - 1}
              </p>
            </div>

            {status === 'idle' && (
              <div className="text-center py-12">
                <DocumentChartBarIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">
                  Select locations and period, then click "Run Report" to generate results.
                </p>
              </div>
            )}

            {status === 'running' && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-600 mb-4" />
                <p className="text-gray-500">{runningMessage}</p>
              </div>
            )}

            {status === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error || 'An error occurred while generating the report.'}</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                >
                  Try again
                </button>
              </div>
            )}

            {status === 'complete' && reportData && (
              <WholesaleComparisonReport data={reportData.data as any} />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between print:hidden">
          <ReportExportButtons
            onPrintPDF={handlePrintPDF}
            onExportExcel={handleExportCSV}
            disabled={!hasResults}
          />
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
